import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { PopupDialogComponent } from "@shared/components/popup-dialog/popup-dialog.component";
import { CNI, RIB } from "@shared/constants/documents.constants";
import { ACCUEIL, CONTRACTDETAILS, QUOTATIONDETAILS } from "@shared/constants/route.constants";
import { AgreementTypes } from "@shared/enums/agreement-types";
import { DocumentStates } from "@shared/enums/document-state";
import { DocumentType } from "@shared/enums/documents.type";
import { RequestNatureOptions } from "@shared/enums/request-nature-options";
import { RequestReasons } from "@shared/enums/request-reason-options";
import { TaskOrigins } from "@shared/enums/task-origins";
import { ContactUsHelper } from "@shared/helpers/contact-us.helper";
import { AgreementContactUs } from "@shared/models/agreement-contact-us.model";
import { BaseRef } from "@shared/models/BaseRef";
import { RequestConfirmationSummary } from "@shared/models/operational-tasks";
import { Policy } from "@shared/models/policy";
import { Quotation } from "@shared/models/quotation";
import { RequestReason, RequestType } from "@shared/models/request-types.model";
import { ContactDataService } from "@shared/services/contact-data.service";
import { ContactFormService } from "@shared/services/contact-form.service";
import { HandlerService } from "@shared/services/handler.service";
import { OperationalTasksService } from "@shared/services/operational-tasks.service";
import { selectPolicyId } from "@shared/state/context/context.selector";
import { filterAgreementVisibility } from "@shared/utils/brand-utils";
import { Guid } from "guid-typescript";
import { ButtonModule } from "primeng/button";
import { CalendarModule } from "primeng/calendar";
import { DropdownModule } from "primeng/dropdown";
import { InputTextModule } from "primeng/inputtext";
import { MessagesModule } from "primeng/messages";
import { forkJoin, lastValueFrom, Observable, take } from "rxjs";
import { ActionButtonsComponent } from "./action-buttons/action-buttons.component";
import { AttestationDetailsComponent } from "./attestation-details/attestation-details.component";
import { CommentSectionComponent } from "./comment-section/comment-section.component";
import { ContactInfoNoteComponent } from "./contact-info-note/contact-info-note.component";
import { ContactReferenceRequestComponent } from "./contact-reference-request/contact-reference-request.component";
import { FileUploadSectionComponent } from "./file-upload-section/file-upload-section.component";
import { RequestConfirmationPopupComponent } from "./termination-confirmation-popup/request-confirmation-popup.component";
import { TerminationDateComponent } from "./termination-date/termination-date.component";
import { TerminationSectionComponent } from "./termination-section/termination-section.component";

@Component({
  selector: "app-contact-us",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    FormsModule,
    InputTextModule,
    CalendarModule,
    TranslateModule,
    PopupDialogComponent,
    MessagesModule,
    RequestConfirmationPopupComponent,
    ContactInfoNoteComponent,
    ContactReferenceRequestComponent,
    TerminationSectionComponent,
    AttestationDetailsComponent,
    TerminationDateComponent,
    FileUploadSectionComponent,
    CommentSectionComponent,
    ActionButtonsComponent,
  ],
  templateUrl: "./contact-us.component.html",
  styleUrls: ["./contact-us.component.scss"],
})
export class ContactUsComponent implements OnInit {
  policyId!: Guid;
  quotationId!: Guid;
  requestNature: string | null = null;

  RequestNatureOptions = RequestNatureOptions;

  contactForm: FormGroup;

  enableSubmit = false;
  enableDocumentUpload = false;

  memberAgreements: AgreementContactUs[] = [];
  requestNatureOptions: RequestType[] = [];
  reasonOptions: RequestReason[] = [];
  showTerminationConfirmationPopup = false;
  showSentRequestPopup = false;
  sentRequestMessage!: any;
  confirmationSummary!: RequestConfirmationSummary;

  minTerminationDate: Date | null = null;
  maxTerminationDate: Date | null = null;
  AgreementTypes = AgreementTypes;
  agreementTypes!: AgreementTypes;
  officeHours!: string;
  advisorPhoneNumber!: string;
  showDocumentInfoPopup = false;
  uploadedFiles: { file: File; docType: string }[] = [];
  mandatoryFileTypeCodes: string[] = [];
  returnToPolicyUrl!: string;

  showMandatoryFileUploadMessage = false;
  spanMandatoryComment = false;

  isGeneratedOptions!: { label: string; value: boolean }[];

  readonly selectedReference: string = "selectedReference";
  readonly selectedRequestNature: string = "selectedRequestNature";
  readonly selectedTerminationReason: string = "selectedTerminationReason";
  readonly selectedTerminationDate: string = "selectedTerminationDate";
  readonly selectedAttestationType: string = "selectedAttestationType";
  readonly isAttestationFor: string = "isAttestationFor";
  readonly comment: string = "comment";

  constructor(
    private readonly contactDataService: ContactDataService,
    private readonly contactFormService: ContactFormService,
    private readonly route: ActivatedRoute,
    private readonly operationalTasksService: OperationalTasksService,
    private readonly router: Router,
    private readonly handlerService: HandlerService,
    private readonly translate: TranslateService,
    private store: Store
  ) {
    this.contactForm = this.contactFormService.initializeForm();
  }

  documentTypes: BaseRef[] = [];
  attestationOptions: BaseRef[] = [];
  documentTypesToDisplay: BaseRef[] = [];
  policies: Policy[] = [];
  quotations: Quotation[] = [];

  async ngOnInit() {
    const storedPolicyId = await lastValueFrom(this.store.select(selectPolicyId).pipe(take(1)));
    this.route.queryParams.subscribe(params => {
      this.policyId = params["policyId"] || storedPolicyId || null;
      this.quotationId = params["quotationId"] || null;
      this.requestNature = params["requestNature"] || null;
    });

    this.loadInitialData();

    this.contactForm.valueChanges.subscribe(() => {
      this.updateEnableSubmit();
    });
  }

  private loadInitialData(): void {
    forkJoin({
      agreements: this.contactDataService.loadMemberAgreements(),
      documentTypes: this.operationalTasksService.getDocumentTypes(),
    }).subscribe({
      next: ({ agreements, documentTypes }) => {
        this.documentTypes = documentTypes;

        const data = filterAgreementVisibility(agreements, this.policyId);
        this.policies = data.policies;
        this.quotations = data.quotations;
        this.memberAgreements = data.agreements;
        const referenceId = this.policyId || this.quotationId;
        this.setReferenceOnForm(referenceId);
      },
      error: error => {
        console.error("Error loading initial data:", error);
      },
    });
  }

  private setReferenceOnForm(agreementId?: Guid | null): void {
    if (!agreementId) return;

    const agreement = this.memberAgreements.find(a => a.id === agreementId);
    if (agreement) {
      this.contactForm.get(this.selectedReference)?.setValue(agreement.id);
      this.onSelectReference();
    }
  }

  onSubmit() {
    if (!this.contactForm.valid) {
      return;
    }
    const selectedRequestNature = this.contactForm.get(this.selectedRequestNature)?.value;

    const requestNatureId = this.requestNatureOptions.find(a => a.code === selectedRequestNature)?.id ?? 0;

    const commonData = {
      originId: TaskOrigins.AUTOMATIC,
      comment: this.contactForm.get(this.comment)?.value,
    };
    // For Termination Task
    if (this.requestNature === RequestNatureOptions.Termination) {
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 2);

      const requestReasonId = this.reasonOptions.find(
        a => a.code == this.contactForm.get(this.selectedTerminationReason)?.value
      )!.id;

      const requestData = {
        policyId: this.contactForm.get(this.selectedReference)?.value,
        ...commonData,
        requestReasonId: requestReasonId,
        dueDate: dueDate.toISOString(),
        terminationRequestedDate: this.contactForm.get(this.selectedTerminationDate)?.value.toISOString(),
        terminationFilesDataFromMember: this.mapFilesData(true),
      };

      this.operationalTasksService.createPolicyTerminationTask(requestData).subscribe({
        next: () => this.displaySentRequestPopup(),
        error: error => console.error("Error creating policy termination task:", error),
      });
    } else if (this.isClientRequestNatureTask()) {
      const requestData = {
        ...commonData,
        policyOrQuotationId: this.contactForm.get(this.selectedReference)?.value,
        agreementType: this.agreementTypes,
        requestNature: requestNatureId,
        attestationType: this.contactForm.get(this.selectedAttestationType)?.value,
        isGeneratedForMe: this.contactForm.get(this.isAttestationFor)?.value,
        filesData: this.mapFilesData(this.requestNature === RequestNatureOptions.ContractualDocuments),
      };

      this.operationalTasksService.createClientRequestTask(requestData).subscribe({
        next: () => this.displaySentRequestPopup(),
        error: error => console.error("Error creating client request task:", error),
      });
    }
    // For complaints Task
    else if (this.requestNature === RequestNatureOptions.Claim) {
      const requestData = {
        policyId: this.contactForm.get(this.selectedReference)?.value,
        ...commonData,
        requestNature: requestNatureId,
        filesData: this.mapFilesData(),
      };

      this.operationalTasksService.createComplaintTask(requestData).subscribe({
        next: () => this.displaySentRequestPopup(),
        error: error => console.error("Error creating complaint task:", error),
      });
    }
  }

  isClientRequestNatureTask(): boolean {
    return (
      this.requestNature === RequestNatureOptions.Attestation ||
      this.requestNature === RequestNatureOptions.Attachments ||
      this.requestNature === RequestNatureOptions.Question ||
      this.requestNature === RequestNatureOptions.ContractualDocuments
    );
  }

  onSelectReference() {
    this.contactFormService.resetFormControls(this.contactForm, [
      this.selectedRequestNature,
      this.comment,
      this.selectedTerminationReason,
      this.selectedTerminationDate,
      this.selectedAttestationType,
      this.isAttestationFor,
    ]);
    this.spanMandatoryComment = false;
    const refId = this.contactForm.get(this.selectedReference)?.value;
    const memberAgreement = this.memberAgreements.find(a => a.id == refId);
    this.requestNature = "";
    if (memberAgreement) {
      this.getHandlerInfos(memberAgreement.id);
      this.agreementTypes = memberAgreement.agreementType;
      memberAgreement.agreementType === AgreementTypes.Policy
        ? (this.policyId = memberAgreement.id)
        : (this.quotationId = memberAgreement.id);
      this.loadRequestTypes(() =>
        this.contactDataService.getRequestTypes(memberAgreement.id, memberAgreement.agreementType)
      );
    } else {
      this.contactFormService.intializeController(this.contactForm, this.selectedRequestNature);
    }
  }

  private loadRequestTypes(requestTypesFn: () => Observable<RequestType[]>) {
    requestTypesFn().subscribe((requestTypesWithReasons: RequestType[]) => {
      this.requestNatureOptions = requestTypesWithReasons;
      this.contactFormService.intializeController(this.contactForm, this.selectedRequestNature);
      this.checkAndSetRequestNature();
    });
  }

  private checkAndSetRequestNature() {
    if (this.requestNature) {
      const natureExists = this.requestNatureOptions.some(opt => opt.code === this.requestNature);
      if (natureExists) {
        this.contactForm.get(this.selectedRequestNature)?.setValue(this.requestNature);
        this.onSelectNatureDemand();
      }
    }
  }

  onSelectNatureDemand() {
    this.contactFormService.resetFormControls(this.contactForm, [
      this.comment,
      this.selectedTerminationReason,
      this.selectedTerminationDate,
      this.selectedAttestationType,
      this.isAttestationFor,
    ]);
    this.spanMandatoryComment = false;
    this.removeAllFiles();
    this.requestNature = this.contactForm.get(this.selectedRequestNature)?.value;
    this.reasonOptions = this.requestNatureOptions.find(a => a.code === this.requestNature)!.reasons;
    if (this.requestNature === RequestNatureOptions.Termination) {
      const currentPolicy = this.policies.find(a => a.id == this.policyId);
      this.reasonOptions = ContactUsHelper.handleRenoncReason(currentPolicy, this.reasonOptions);
      this.contactForm.get(this.selectedTerminationReason)?.setValue("");
      this.contactForm.get(this.selectedTerminationDate)?.setValue("");
      this.maxTerminationDate = null;
      this.minTerminationDate = null;

      this.contactFormService.setValidator(this.contactForm, this.selectedTerminationReason, [Validators.required]);
    } else if (this.requestNature === RequestNatureOptions.Attestation) {
      this.attestationOptions = ContactUsHelper.updateAttestationOptions(
        this.requestNatureOptions,
        this.documentTypes,
        RequestNatureOptions.Attestation
      );
      this.isGeneratedOptions = [
        { label: this.translate.instant("generalOptions.yes"), value: true },
        { label: this.translate.instant("generalOptions.no"), value: false },
      ];

      this.contactFormService.setValidators(
        this.contactForm,
        [this.selectedAttestationType, this.isAttestationFor],
        [Validators.required]
      );

      this.contactFormService.updateAttestationTrame(this.contactForm);
      this.onNatureSelectedOtherThenTermination();
    } else {
      if (this.requestNature === RequestNatureOptions.Claim || this.requestNature === RequestNatureOptions.Question) {
        this.contactForm.get(this.comment)?.setValidators(Validators.required);
        this.spanMandatoryComment = true;
      }

      this.contactForm.get(this.comment)?.updateValueAndValidity();

      this.onNatureSelectedOtherThenTermination();
    }

    this.contactFormService.enableControl(this.contactForm, this.comment);

    this.contactForm.updateValueAndValidity();

    this.updateEnableSubmit();
    this.enableDocumentUpload = true;
  }

  onSelectTerminationReason() {
    this.removeAllFiles();
    const selectedTerminationReason = this.contactForm.get(this.selectedTerminationReason)?.value;

    this.showMandatoryFileUploadMessage = false;
    this.documentTypesToDisplay = this.documentTypes.filter(d => d.code == DocumentType.AUTRE);
    this.mandatoryFileTypeCodes = this.reasonOptions.find(
      a => a.code == selectedTerminationReason
    )!.supportingDocumentsCodes;

    if (this.mandatoryFileTypeCodes.length) {
      this.showMandatoryFileUploadMessage = true;
      this.documentTypesToDisplay = this.documentTypes
        .filter(d => this.mandatoryFileTypeCodes.includes(d.code))
        .map(d => {
          if (d.code === CNI) {
            return { ...d, shortLabel: CNI };
          }
          if (d.code === RIB) {
            return { ...d, shortLabel: RIB };
          }
          return d;
        });
    }

    const currentPolicy = this.policies.find(a => a.id == this.policyId);
    const constraints = ContactUsHelper.getTerminationDateConstraints(selectedTerminationReason, currentPolicy);

    const terminationDateControl = this.contactForm.get(this.selectedTerminationDate);
    setTimeout(() => {
      terminationDateControl?.setValue(constraints.defaultDate);
    });
    if (constraints.disabled) {
      terminationDateControl?.disable();
    } else {
      terminationDateControl?.enable();
    }

    this.minTerminationDate = constraints.minDate;
    this.maxTerminationDate = constraints.maxDate;
    this.updateEnableSubmit();
  }

  onNatureSelectedOtherThenTermination() {
    const fileTypeCodes = this.reasonOptions.find(a => a.code === RequestReasons.NONE)!.supportingDocumentsCodes;

    if (fileTypeCodes.length) {
      this.showMandatoryFileUploadMessage =
        this.requestNature === RequestNatureOptions.Attachments ||
        this.requestNature === RequestNatureOptions.ContractualDocuments;
      this.mandatoryFileTypeCodes = this.showMandatoryFileUploadMessage ? fileTypeCodes : [];

      this.documentTypesToDisplay = this.documentTypes
        .filter(d => fileTypeCodes.includes(d.code))
        .map(d => {
          if (d.code === CNI) {
            return { ...d, shortLabel: CNI };
          }
          if (d.code === RIB) {
            return { ...d, shortLabel: RIB };
          }
          return d;
        });
    }
  }

  isFileUploadValid(): boolean {
    if (this.mandatoryFileTypeCodes.length === 0) {
      return true;
    }
    const isValid = this.uploadedFiles.some(file => this.mandatoryFileTypeCodes.includes(file.docType));
    return isValid;
  }

  removeAllFiles() {
    this.uploadedFiles = [];
    this.updateEnableSubmit();
  }

  updateEnableSubmit() {
    // Check if the request nature is Termination
    if (this.requestNature === RequestNatureOptions.Termination) {
      // Retrieve the termination date control
      const terminationDateControl = this.contactForm.get(this.selectedTerminationDate);
      const terminationDateValue = terminationDateControl?.value;
      const terminationDateValid = terminationDateValue !== null;

      // Check form validity
      const formValid = this.contactForm.valid;
      // Check file upload validity
      const fileUploadValid = this.isFileUploadValid();
      // Determine enableSubmit status
      this.enableSubmit = formValid && terminationDateValid && fileUploadValid;
    } else {
      this.enableSubmit = this.contactForm.valid && this.isFileUploadValid();
    }
  }

  goBackToPolicy() {
    const data = this.policies.find(policy => policy.id === this.policyId);

    if (data) {
      const url = CONTRACTDETAILS.replace(":id", data!.id.toString());
      this.router.navigate([url], {
        state: { data },
      });
    }
  }

  goBackToQuotation() {
    const data = this.quotations.find(quotation => quotation.id === this.quotationId);

    if (data) {
      const url = QUOTATIONDETAILS.replace(":id", data!.id.toString());
      this.router.navigate([url], {
        state: { data: data },
      });
    }
  }

  getHandlerInfos(agreementId?: Guid) {
    this.handlerService.getHandler(agreementId).subscribe(handler => {
      this.officeHours = handler.officeHours;
      this.advisorPhoneNumber = handler.phone;
    });
  }

  handleTerminationConfirmationPopup(confirmed: boolean) {
    if (confirmed) {
      this.onSubmit();
    }

    this.showTerminationConfirmationPopup = false;
  }

  handleTerminationConfirmation(summary: RequestConfirmationSummary) {
    this.confirmationSummary = summary;
    this.showTerminationConfirmationPopup = true;
  }

  displaySentRequestPopup() {
    this.sentRequestMessage = {
      header: this.translate.instant("requestSent.title"),
      message:
        this.translate.instant("requestSent.confirmationMail") +
        this.translate.instant("requestSent.contactInfo", {
          officeHours: this.officeHours ?? "",
          advisorPhoneNumber: this.advisorPhoneNumber ?? "",
        }),
    };

    this.showSentRequestPopup = true;
  }

  onCloseSentRequestPopup() {
    this.showSentRequestPopup = false;
    this.closeContactUsPage();
  }

  closeContactUsPage() {
    if (this.agreementTypes === AgreementTypes.Policy) {
      this.goBackToPolicy();
    } else if (this.agreementTypes === AgreementTypes.Quotation) {
      this.goBackToQuotation();
    } else {
      this.router.navigate([ACCUEIL]);
    }
  }

  onFilesChanged(files: { file: File; docType: string }[]): void {
    this.uploadedFiles = files;
    this.updateEnableSubmit();
  }

  mapFilesData = (conditionalState = false) =>
    this.uploadedFiles.map(uploadedFile => {
      const documentType = this.documentTypes.find(type => type.code === uploadedFile.docType);
      return {
        file: uploadedFile.file,
        documentTypeId: documentType?.id,
        ...(conditionalState && { documentStateId: DocumentStates.TOBEREVIEWED }),
      };
    });
}
